import React, { useState, useRef, useEffect } from "react";
import {
  ChevronDown,
  MessageSquare,
  MoreVertical,
  Sun,
  Moon,
  Info,
  LogIn,
  LogOut,
  FileText,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "./ui/LanguageSelector";
import { WIDGET_VERSION } from "@/config/security";
import { getLogoSrc } from "@/utils/helpers";

export const ChatHeader = ({
  onClose,
  theme,
  onToggleTheme,
  showBranding = true,
  widgetConfig = null,
  onToggleConversations,
  showConversations = false,
  conversationsCount = 0,
  onToggleAbout,
  showAbout = false,
  version = WIDGET_VERSION,
  onToggleMcpLogin,
  showMcpLogin = false,
  mcpLoggedIn = false,
  onOpenTiaCore,
  tiaCoreLoading = false,
  showTiaCoreButton = false,
}) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const companyName =
    widgetConfig?.widget_customization?.branding?.company_name ||
    t("widget.title");
  const customTitle =
    widgetConfig?.widget_customization?.branding?.custom_title;
  const companyLogo =
    widgetConfig?.widget_customization?.branding?.company_logo_base64;
  const displayTitle = customTitle || companyName;

  const logoSrc = getLogoSrc(companyLogo);

  const headerBgColor =
    widgetConfig?.widget_customization?.colors?.header_background;
  const headerTextColor =
    widgetConfig?.widget_customization?.colors?.header_text;

  const headerStyles = {};
  if (headerBgColor) headerStyles.backgroundColor = headerBgColor;
  if (headerTextColor) headerStyles.color = headerTextColor;

  const handleMenuAction = (e, action) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuOpen(false);
    action();
  };

  return (
    <div className="gpc-chat-header" style={headerStyles}>
      <div className="gpc-header-content">
        {logoSrc && (
          <div className="gpc-header-logo">
            <img src={logoSrc} alt={companyName} className="gpc-company-logo" />
          </div>
        )}
        <div className="gpc-header-text">
          <h2
            className="gpc-header-title"
            style={headerTextColor ? { color: headerTextColor } : {}}
          >
            {displayTitle}
          </h2>
        </div>
      </div>

      <div className="gpc-header-actions">
        {onToggleConversations && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleConversations();
            }}
            className={`gpc-header-button gpc-focus-visible gpc-transition relative ${showConversations ? "bg-blue-500 text-white" : ""}`}
            aria-label={t("conversations.toggle", "Toggle conversations")}
            title={t("conversations.toggle", "Toggle conversations")}
          >
            <MessageSquare className="gpc-icon" size={20} />
            {conversationsCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                {conversationsCount > 9 ? "9+" : conversationsCount}
              </span>
            )}
          </button>
        )}

        <LanguageSelector />

        {showTiaCoreButton && mcpLoggedIn && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onOpenTiaCore) onOpenTiaCore();
            }}
            className="gpc-header-button gpc-focus-visible gpc-transition"
            aria-label={t("tiaCore.openPortal")}
            title={t("tiaCore.openPortal")}
            disabled={tiaCoreLoading}
          >
            {tiaCoreLoading ? (
              <div className="gpc-spinner gpc-button-spinner" style={{ width: 18, height: 18 }} />
            ) : (
              <FileText className="gpc-icon" size={20} />
            )}
          </button>
        )}

        {/* Menú ⋮ */}
        <div className="gpc-header-menu" ref={menuRef}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setMenuOpen((p) => !p);
            }}
            className={`gpc-header-button gpc-focus-visible gpc-transition ${menuOpen ? "bg-blue-500 text-white" : ""}`}
            aria-label={t("ui.menu")}
            title={t("ui.menu")}
          >
            <MoreVertical className="gpc-icon" size={20} />
          </button>

          {menuOpen && (
            <div className="gpc-header-dropdown">
              {/* Tema */}
              <button
                type="button"
                className="gpc-header-dropdown-item"
                onClick={(e) => handleMenuAction(e, onToggleTheme)}
              >
                {theme === "light" ? (
                  <Moon size={16} className="gpc-header-dropdown-icon" />
                ) : (
                  <Sun size={16} className="gpc-header-dropdown-icon" />
                )}
                <span>{t("theme.toggle")}</span>
              </button>

              {/* About */}
              {onToggleAbout && (
                <>
                  <div className="gpc-header-dropdown-divider" />
                  <button
                    type="button"
                    className={`gpc-header-dropdown-item ${showAbout ? "gpc-header-dropdown-item-active" : ""}`}
                    onClick={(e) => handleMenuAction(e, onToggleAbout)}
                  >
                    <Info size={16} className="gpc-header-dropdown-icon" />
                    <span>{t("about.title")}</span>
                  </button>
                </>
              )}

              {/* MCP login/logout */}
              {onToggleMcpLogin && (
                <>
                  <div className="gpc-header-dropdown-divider" />
                  <button
                    type="button"
                    className="gpc-header-dropdown-item"
                    onClick={(e) => handleMenuAction(e, onToggleMcpLogin)}
                  >
                    {mcpLoggedIn ? (
                      <LogOut size={16} className="gpc-header-dropdown-icon" />
                    ) : (
                      <LogIn size={16} className="gpc-header-dropdown-icon" />
                    )}
                    <span>
                      {mcpLoggedIn ? t("moodle.logout") : t("moodle.login")}
                    </span>
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          className="gpc-header-button gpc-focus-visible gpc-transition"
          aria-label={t("chat.close")}
          title={t("chat.close")}
        >
          <ChevronDown className="gpc-icon" />
        </button>
      </div>
    </div>
  );
};
